<template>
  <div class="space-y-8">
    <div class="text-center">
      <!--<h1 class="text-4xl font-bold text-gray-900 dark:text-white">
        {{ t('home.welcome') }}
      </h1>-->
      <p class="mt-4 text-xl text-gray-600 dark:text-gray-300">
        {{ t('home.subtitle') }}
      </p>
    </div>

    <!-- Quick Search -->
    <QuickSearch />

    <!-- Category Grid -->
    <CategoryGrid />

    <!-- Featured Listings Section -->
    <div>
      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary-500"></div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="text-center">
        <div class="text-red-500">{{ error }}</div>
        <button
          @click="retryFetch"
          class="btn-primary mt-4"
        >
          {{ t('home.try_again') }}
        </button>
      </div>

      <!-- Featured Listings -->
      <div v-else-if="featuredListings.length > 0" class="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
        <div 
          v-for="listing in featuredListings" 
          :key="listing.id" 
          class="overflow-hidden rounded-lg bg-white shadow-lg dark:bg-gray-800 flex flex-col h-[500px]"
        >
          <div class="h-48 w-full">
            <template v-if="listing.images?.[0]">
              <img 
                :src="listing.images[0].startsWith('/') ? listing.images[0] : `/${listing.images[0]}`"
                :alt="listing.title" 
                class="h-full w-full object-cover" 
              />
            </template>
            <ImagePlaceholder v-else />
          </div>
          <div class="p-6 flex-grow flex flex-col">
            <div class="flex items-center justify-between mb-2">
              <h3 
                class="text-xl font-semibold text-gray-900 dark:text-white"
                :class="{
                  'text-right font-[Vazirmatn]': isTextRTL(listing.title),
                  'text-left': !isTextRTL(listing.title)
                }"
              >
                {{ listing.title }}
              </h3>
              <div class="flex items-center space-x-2">
                <span v-if="listing.featured" class="text-yellow-400">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5">
                    <path fill-rule="evenodd" d="M10.868 2.884c-.321-.772-1.415-.772-1.736 0l-1.83 4.401-4.753.381c-.833.067-1.171 1.107-.536 1.651l3.62 3.102-1.106 4.637c-.194.813.691 1.456 1.405 1.02L10 15.591l4.069 2.485c.713.436 1.598-.207 1.404-1.02l-1.106-4.637 3.62-3.102c.635-.544.297-1.584-.536-1.65l-4.752-.382-1.831-4.401z" clip-rule="evenodd" />
                  </svg>
                </span>
                <span v-if="listing.isPromoted" class="text-secondary-400">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5">
                    <path fill-rule="evenodd" d="M13.5 4.938a7 7 0 11-9.006 1.737c.202-.257.59-.218.793.039.278.352.594.672.943.954.332.269.786-.049.773-.476a5.977 5.977 0 01.572-2.759 6.026 6.026 0 012.486-2.665c.247-.14.55-.016.677.238A6.967 6.967 0 0013.5 4.938zM14 12a4 4 0 01-4 4c-1.913 0-3.52-1.398-3.91-3.182-.093-.429.44-.643.814-.413a4.043 4.043 0 001.601.564c.303.038.531-.24.51-.544a5.975 5.975 0 011.315-4.192.447.447 0 01.431-.129A4.001 4.001 0 0114 12z" clip-rule="evenodd" />
                  </svg>
                </span>
              </div>
            </div>
            <p 
              class="mt-2 text-gray-600 dark:text-gray-300 line-clamp-2"
              :class="{
                'text-right font-[Vazirmatn]': isTextRTL(listing.description),
                'text-left': !isTextRTL(listing.description)
              }"
            >
              {{ stripHtml(listing.description) }}
            </p>
            <div class="mt-4 flex flex-col space-y-2">
              <div class="flex items-center text-gray-600 dark:text-gray-300 text-sm">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4 mr-1">
                  <path fill-rule="evenodd" d="M9.69 18.933l.003.001C9.89 19.02 10 19 10 19s.11.02.308-.066l.002-.001.006-.003.018-.008a5.741 5.741 0 00.281-.14c.186-.096.446-.24.757-.433.62-.384 1.445-.966 2.274-1.765C15.302 14.988 17 12.493 17 9A7 7 0 103 9c0 3.492 1.698 5.988 3.355 7.584a13.731 13.731 0 002.273 1.765 11.842 11.842 0 00.976.544l.062.029.018.008.006.003zM10 11.25a2.25 2.25 0 100-4.5 2.25 2.25 0 000 4.5z" clip-rule="evenodd" />
                </svg>
                {{ listing.location || t('listings.locationNotSpecified') }}
              </div>
              <div class="flex items-center text-gray-600 dark:text-gray-300 text-sm">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4 mr-1">
                  <path fill-rule="evenodd" d="M5.75 2a.75.75 0 01.75.75V4h7V2.75a.75.75 0 011.5 0V4h.25A2.75 2.75 0 0118 6.75v8.5A2.75 2.75 0 0115.25 18H4.75A2.75 2.75 0 012 15.25v-8.5A2.75 2.75 0 014.75 4H5V2.75A.75.75 0 015.75 2zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75z" clip-rule="evenodd" />
                </svg>
                {{ formatDate(listing.createdAt) }}
              </div>
              <div class="mt-2">
                <span class="text-lg font-bold text-secondary-600 dark:text-secondary-400" :class="{ 'font-[Vazirmatn]': isTextRTL(t('listings.negotiable')) }">
                  <template v-if="listing.agreement_based">
                    {{ t('listings.negotiable') }}
                  </template>
                  <template v-else>
                    {{ getCurrencySymbol(listing.currency || 'USD') }}{{ formatPrice(listing.price) }}
                  </template>
                </span>
              </div>
            </div>
            <div class="mt-auto pt-4">
              <router-link
                :to="{ name: 'listing-detail', params: { id: listing.id, title: listing.title }}"
                class="btn-primary px-6 py-2 rounded-lg shadow-md hover:shadow-lg hover:scale-105 transform transition-all duration-200 ease-in-out w-full text-center block"
                :dir="isTextRTL(t('listings.viewDetails')) ? 'rtl' : 'ltr'"
              >
                <span class="flex justify-center items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5 mr-1">
                    <path fill-rule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clip-rule="evenodd" />
                  </svg>
                  {{ t('listings.viewDetails') }}
                </span>
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- No Featured Listings -->
      <div v-else class="text-center py-8">
        <p class="text-lg text-gray-600 dark:text-gray-300">
          {{ t('home.start_exploring') }}
        </p>
      </div>
    </div>

    <div class="text-center md:mb-0 mb-40">
      <router-link
        to="/listings"
        class="btn-primary inline-flex items-center px-8 py-4 text-lg font-bold rounded-lg shadow-lg hover:shadow-xl hover:scale-105 transform transition-all duration-200 ease-in-out md:static md:relative"
      >
        {{ t('listings.browse_all_listings') }}
        <svg
          class="ml-2 h-5 w-5"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
            clip-rule="evenodd"
          />
        </svg>
      </router-link>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useTranslation } from '@/composables/useTranslation'
import { useListingsStore } from '@/store/modules/listings'
import type { Listing } from '@/store/modules/listings'
import ImagePlaceholder from '@/components/common/ImagePlaceholder.vue'
import QuickSearch from '@/components/search/QuickSearch.vue'
import CategoryGrid from '@/components/categories/CategoryGrid.vue'
import { stripHtml } from '@/utils/html'
import { isRTL } from '@/utils/text'
import { format } from 'timeago.js'

const { t } = useTranslation()
const listingsStore = useListingsStore()

// State
const loading = ref(false)
const error = ref<string | null>(null)
const retryCount = ref(0)
const maxRetries = 2

// Computed
const featuredListings = computed(() => listingsStore.items || [])

// Computed property to check if text is RTL
const isTextRTL = computed(() => {
  return (text: string) => isRTL(text)
})

// Format date helper
const formatDate = (date: string) => format(new Date(date))

// Helper function to get currency symbol
const getCurrencySymbol = (currency: string): string => {
  switch (currency) {
    case 'GBP': return '£';
    case 'USD': return '$';
    case 'EUR': return '€';
    case 'TRY': return '₺';
    default: return '$';
  }
}

// Format price with commas as thousand separators
const formatPrice = (price: number): string => {
  return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

// Methods
const fetchFeaturedListings = async () => {
  if (!loading.value) {
    try {
      loading.value = true
      error.value = null
      
      // Set a timeout to prevent infinite loading
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error('Request timed out'));
        }, 3000);
      });
      
      // Race between the actual request and the timeout
      await Promise.race([
        listingsStore.fetchListings({
          // featured: true,
          status: 'active',
          sortBy: 'created_at',
          page: 1,
          pageSize: 6
        }),
        timeoutPromise
      ]);
      
    } catch (err) {
      console.error('Error fetching featured listings:', err)
      error.value = t('home.failed_to_load')
      
      // Auto-retry if we haven't exceeded max retries
      if (retryCount.value < maxRetries) {
        retryCount.value++
        setTimeout(() => {
          fetchFeaturedListings()
        }, 2000) // Wait 2 seconds before retrying
      }
    } finally {
      loading.value = false
    }
  }
}

const retryFetch = () => {
  retryCount.value = 0 // Reset retry count on manual retry
  fetchFeaturedListings()
}

// Initialize immediately without delay
onMounted(() => {
  // Start fetching immediately in the background
  fetchFeaturedListings()
})
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Vazirmatn:wght@400;500;600;700&display=swap');

/* Ensure the Browse All Listings button is fully visible on mobile */
@media (max-width: 768px) {
  .space-y-8 > div:last-child {
    margin-bottom: 10rem !important;
  }
}
</style>
